import { type ParsedMessage } from '../lib/chat/MessageParser';

interface EventSubChatMessage {
  broadcaster_user_id: string;
  broadcaster_user_login: string;
  broadcaster_user_name: string;
  chatter_user_id: string;
  chatter_user_login: string;
  chatter_user_name: string;
  message_id: string;
  message: {
    text: string;
    fragments: Array<{
      type: string;
      text: string;
      cheermote?: any;
      emote?: any;
      mention?: any;
    }>;
  };
  color: string;
  badges: Array<{
    set_id: string;
    id: string;
    info: string;
  }>;
  message_type: string;
  cheer?: any;
  reply?: any;
  channel_points_custom_reward_id?: string;
}

interface EventSubChatNotification {
  broadcaster_user_id: string;
  broadcaster_user_login: string;
  broadcaster_user_name: string;
  chatter_user_id: string;
  chatter_user_login: string;
  chatter_user_name: string;
  message_id: string;
  message: {
    text: string;
    fragments: Array<{
      type: string;
      text: string;
      cheermote?: any;
      emote?: any;
      mention?: any;
    }>;
  };
  color: string;
  badges: Array<{
    set_id: string;
    id: string;
    info: string;
  }>;
  notice_type: string; // This should be 'announcement' for announcements
  announcement?: {
    color: string; // 'primary', 'blue', 'green', 'orange', 'purple'
  };
}

class TwitchEventSubService {
  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private readonly url: string = "wss://eventsub.wss.twitch.tv/ws";
  private readonly clientId: string = import.meta.env.VITE_TWITCH_CLIENT_ID || '';
  private readonly accessToken: string = localStorage.getItem('twitch_access_token') || '';
  private chatMessageHandlers: Set<(message: ParsedMessage) => void> = new Set();
  private connectionStateHandlers: Set<(connected: boolean) => void> = new Set();
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private isConnected = false;

  constructor() {
    if (!this.clientId) {
      throw new Error("Client ID is missing. Please set VITE_TWITCH_CLIENT_ID in environment variables.");
    }
    if (!this.accessToken) {
      throw new Error("Access token is missing. Please authenticate before using EventSub.");
    }
  }

  // Public API for chat integration
  public onChatMessage(handler: (message: ParsedMessage) => void): () => void {
    this.chatMessageHandlers.add(handler);
    return () => this.chatMessageHandlers.delete(handler);
  }

  public onConnectionStateChange(handler: (connected: boolean) => void): () => void {
    this.connectionStateHandlers.add(handler);
    return () => this.connectionStateHandlers.delete(handler);
  }

  public isEventSubConnected(): boolean {
    return this.isConnected;
  }

  async connect(): Promise<void> {
    try {
      console.log("[EventSub] Connecting to Twitch EventSub WebSocket...");
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log("[EventSub] WebSocket connection established");
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        console.log("[EventSub] WebSocket message received:", event.data);
        this.handleMessage(event);
      };

      this.ws.onclose = (event) => {
        console.error(
          `[EventSub] WebSocket connection closed. Code: ${event.code}, Reason: ${event.reason || "No reason provided"}`
        );
        this.isConnected = false;
        this.notifyConnectionStateChange(false);
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
          console.log(`[EventSub] Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`);
          setTimeout(() => this.connect(), delay);
        } else {
          console.error("[EventSub] Max reconnection attempts reached");
        }
      };

      this.ws.onerror = (error) => {
        console.error("[EventSub] WebSocket error:", error);
      };
    } catch (error) {
      console.error("[EventSub] Error setting up WebSocket connection:", error);
    }
  }

  private notifyConnectionStateChange(connected: boolean): void {
    this.connectionStateHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        console.error("[EventSub] Error in connection state handler:", error);
      }
    });
  }

  private async createSubscriptions(): Promise<void> {
    const user = JSON.parse(localStorage.getItem('twitch_user') || '{}');
    const broadcasterId = user.id;
    const moderatorId = user.id; // Assuming the authenticated user is the moderator

    if (!broadcasterId) {
      throw new Error("Broadcaster user ID not found");
    }

    if (!moderatorId) {
      throw new Error("Moderator user ID not found");
    }

    const subscriptionPayloads = [
      {
        type: "channel.follow",
        version: "2",
        condition: {
          broadcaster_user_id: broadcasterId,
          moderator_user_id: moderatorId, // Required for channel.follow v2
        },
      },
      {
        type: "channel.subscribe",
        version: "1",
        condition: {
          broadcaster_user_id: broadcasterId,
        },
      },
      // Chat message subscription
      {
        type: "channel.chat.message",
        version: "1",
        condition: {
          broadcaster_user_id: broadcasterId,
          user_id: moderatorId,
        },
      },
      // MISSING: Chat notification subscription (for announcements!)
      {
        type: "channel.chat.notification",
        version: "1",
        condition: {
          broadcaster_user_id: broadcasterId,
          user_id: moderatorId,
        },
      },
    ];

    for (const payload of subscriptionPayloads) {
      try {
        console.log(`[EventSub] Creating subscription: ${payload.type}`);
        console.log(`[EventSub] Subscription condition:`, payload.condition);
        const result = await this.createEventSubSubscription(payload);
        console.log(`[EventSub] Subscription for ${payload.type} created successfully:`, result);
      } catch (error) {
        console.error(`[EventSub] Failed to create subscription for ${payload.type}:`, error);
        
        // Special handling for chat message subscription failure
        if (payload.type === "channel.chat.message") {
          console.error(`[EventSub] Chat message subscription failed! This means EventSub won't receive chat messages.`);
          console.error(`[EventSub] Common causes: missing user:read:chat scope, or permission issues`);
          console.error(`[EventSub] The system will fall back to IRC for chat messages.`);
        }
        
        // Special handling for chat notification subscription failure
        if (payload.type === "channel.chat.notification") {
          console.error(`[EventSub] Chat notification subscription failed! This means EventSub won't receive announcements.`);
          console.error(`[EventSub] Common causes: missing user:read:chat scope, or permission issues`);
          console.error(`[EventSub] The system will fall back to IRC for announcements.`);
        }
      }
    }
  }

  private async createEventSubSubscription(payload: {
    type: string;
    version: string;
    condition: any;
  }): Promise<any> {
    if (!this.sessionId) {
      throw new Error("Session ID is not available. Cannot create subscription.");
    }

    const subscriptionData = {
      type: payload.type,
      version: payload.version,
      condition: payload.condition,
      transport: {
        method: 'websocket',
        session_id: this.sessionId,
      },
    };

    console.log(`[EventSub] Sending subscription request for ${payload.type}:`, subscriptionData);

    const response = await fetch('https://api.twitch.tv/helix/eventsub/subscriptions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Client-Id': this.clientId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscriptionData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[EventSub] Subscription creation failed with status ${response.status}:`, errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        if (errorData.error && errorData.message) {
          throw new Error(`${response.status}: ${errorData.error} - ${errorData.message}`);
        }
      } catch (parseError) {
        // If JSON parsing fails, use the raw error text
      }
      
      throw new Error(`Failed to create EventSub subscription. Status: ${response.status}, Response: ${errorText}`);
    }

    const result = await response.json();
    console.log(`[EventSub] Subscription creation response:`, result);
    return result;
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    const data = JSON.parse(event.data);

    switch (data.metadata.message_type) {
      case "session_welcome":
        this.sessionId = data.payload.session.id;
        console.log("[EventSub] Session established with ID:", this.sessionId);
        this.isConnected = true;
        this.notifyConnectionStateChange(true);
        // Subscriptions can now be created
        await this.createSubscriptions();
        break;
      case "notification":
        this.handleNotification(data.payload);
        break;
      case "session_keepalive":
        console.log("[EventSub] Keepalive received");
        break;
      case "ping":
        this.respondToPing();
        break;
      case "session_reconnect":
        this.connectToReconnectUrl(data.payload.session.reconnect_url);
        break;
      default:
        console.warn("[EventSub] Unhandled message type:", data.metadata.message_type);
    }
  }

  private respondToPing(): void {
    if (this.ws) {
      console.log("[EventSub] Responding to PING");
      this.ws.send("PONG");
    }
  }

  private connectToReconnectUrl(reconnectUrl: string): void {
    console.log("[EventSub] Reconnecting to:", reconnectUrl);
    if (this.ws) {
      this.ws.close();
    }
    this.ws = new WebSocket(reconnectUrl);
    this.ws.onmessage = (event) => this.handleMessage(event);
    this.ws.onclose = (event) => {
      console.error(`[EventSub] Reconnect WebSocket closed: ${event.code} - ${event.reason}`);
      this.isConnected = false;
      this.notifyConnectionStateChange(false);
    };
  }

  private handleNotification(payload: any): void {
    const { subscription, event } = payload;
    console.log("[EventSub] Notification received:", payload);
    
    switch (subscription.type) {
      case "channel.follow":
        console.log(`[EventSub] ${event.user_name} followed the channel!`);
        break;
      case "channel.subscribe":
        console.log(`[EventSub] ${event.user_name} subscribed to the channel!`);
        break;
      case "channel.chat.message":
        console.log(`[EventSub] Chat message from ${event.chatter_user_name}: ${event.message.text}`);
        this.handleChatMessage(event as EventSubChatMessage);
        break;
      case "channel.chat.notification":
        console.log(`[EventSub] 🎯 Chat notification from ${event.chatter_user_name}: ${event.message.text} (${event.notice_type})`);
        this.handleChatNotification(event as EventSubChatNotification);
        break;
      default:
        console.warn("[EventSub] Unhandled notification type:", subscription.type);
    }
  }

  private handleChatMessage(eventData: EventSubChatMessage): void {
    try {
      // Convert EventSub chat message to ParsedMessage format
      const parsedMessage: ParsedMessage = {
        username: eventData.chatter_user_login,
        displayName: eventData.chatter_user_name,
        message: eventData.message.text,
        timestamp: Date.now(),
        messageType: 'chat',
        badges: eventData.badges?.map(badge => ({
          id: badge.set_id,
          version: badge.id,
          info: badge.info
        })),
        // Handle emotes from fragments if needed
        emotes: this.extractEmotesFromFragments(eventData.message.fragments)
      };

      console.log(`[EventSub] Parsed chat message:`, parsedMessage);

      // Notify all chat message handlers
      this.chatMessageHandlers.forEach(handler => {
        try {
          handler(parsedMessage);
        } catch (error) {
          console.error("[EventSub] Error in chat message handler:", error);
        }
      });
    } catch (error) {
      console.error("[EventSub] Error processing chat message:", error);
    }
  }

  private extractEmotesFromFragments(fragments: EventSubChatMessage['message']['fragments']): Record<string, Array<{start: number; end: number; id: string}>> | undefined {
    const emotes: Record<string, Array<{start: number; end: number; id: string}>> = {};
    let currentPosition = 0;
    
    for (const fragment of fragments) {
      if (fragment.type === 'emote' && fragment.emote) {
        const emoteId = fragment.emote.id;
        if (!emotes[emoteId]) {
          emotes[emoteId] = [];
        }
        emotes[emoteId].push({
          start: currentPosition,
          end: currentPosition + fragment.text.length - 1,
          id: emoteId
        });
      }
      currentPosition += fragment.text.length;
    }
    
    return Object.keys(emotes).length > 0 ? emotes : undefined;
  }

  private handleChatNotification(eventData: EventSubChatNotification): void {
    try {
      console.log('[EventSub] 🎯 Processing chat notification:', {
        notice_type: eventData.notice_type,
        chatter: eventData.chatter_user_name,
        message: eventData.message.text,
        announcement_color: eventData.announcement?.color
      });

      // Only handle announcements for now
      if (eventData.notice_type !== 'announcement') {
        console.log('[EventSub] Ignoring non-announcement notification:', eventData.notice_type);
        return;
      }

      // Convert EventSub chat notification to ParsedMessage format
      const parsedMessage: ParsedMessage = {
        username: eventData.chatter_user_login,
        displayName: eventData.chatter_user_name,
        message: eventData.message.text,
        timestamp: Date.now(),
        messageType: 'announcement',
        isAnnouncement: true,
        announcementColor: eventData.announcement?.color || 'primary',
        badges: eventData.badges?.map(badge => ({
          id: badge.set_id,
          version: badge.id,
          info: badge.info
        })),
        // Handle emotes from fragments if needed
        emotes: this.extractEmotesFromFragments(eventData.message.fragments)
      };

      console.log(`[EventSub] 🎯 Parsed announcement:`, {
        isAnnouncement: parsedMessage.isAnnouncement,
        messageType: parsedMessage.messageType,
        announcementColor: parsedMessage.announcementColor,
        message: parsedMessage.message
      });

      // Notify all chat message handlers (same handlers as regular messages)
      this.chatMessageHandlers.forEach(handler => {
        try {
          handler(parsedMessage);
        } catch (error) {
          console.error("[EventSub] Error in chat notification handler:", error);
        }
      });
    } catch (error) {
      console.error("[EventSub] Error processing chat notification:", error);
    }
  }
}

export default TwitchEventSubService;
